import { useTranslation } from 'react-i18next';
import { ChevronRight, Info } from 'lucide-react';
import { SignalVisibilityType } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { SeverityField } from '../create-signal/SeverityField';
import { VisibilityToggle } from '../create-signal/VisibilityToggle';
import { VisibleToField } from '../create-signal/VisibleToField';
import { AnonymousToggle } from '../create-signal/AnonymousToggle';
import { ApplicabilityScopeField } from '../create-signal/ApplicabilityScopeField';
import type { SignalDefaults } from './types';

interface DefaultsStepProps {
  defaults: SignalDefaults;
  signalCount: number;
  onDefaultsChange: (defaults: SignalDefaults) => void;
  onAcceptAll: () => void;
  onReviewIndividually: () => void;
  onCancel: () => void;
}

export const DefaultsStep = ({
  defaults,
  signalCount,
  onDefaultsChange,
  onAcceptAll,
  onReviewIndividually,
  onCancel,
}: DefaultsStepProps) => {
  const { t } = useTranslation('knowledge');

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold">{t('signalReview.title')}</h2>
        <p className="text-[13px] text-muted-foreground">
          {t('signalReview.defaultsDescription', { count: signalCount })}
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
        <Info className="h-[15px] w-[15px] shrink-0 text-primary mt-px" />
        {t('signalReview.defaultsBanner')}
      </div>

      {/* Properties */}
      <div className="flex flex-col">
        <SeverityField
          value={defaults.severity}
          onChange={(severity) => onDefaultsChange({ ...defaults, severity })}
        />
        <VisibilityToggle
          value={defaults.visibility}
          onChange={(visibility) =>
            onDefaultsChange({
              ...defaults,
              visibility,
              viewableBy:
                visibility === SignalVisibilityType.PUBLIC
                  ? { users: [], teams: [] }
                  : defaults.viewableBy,
            })
          }
        />

        {/* Visible To — only when Private */}
        {defaults.visibility === SignalVisibilityType.PRIVATE && (
          <VisibleToField
            value={defaults.viewableBy}
            onChange={(viewableBy) =>
              onDefaultsChange({ ...defaults, viewableBy })
            }
          />
        )}

        <AnonymousToggle
          value={defaults.isAnonymous}
          onChange={(isAnonymous) =>
            onDefaultsChange({ ...defaults, isAnonymous })
          }
        />
      </div>

      {/* Applicability scope (managers only) */}
      <ApplicabilityScopeField
        value={defaults.applicabilityScope}
        onChange={(applicabilityScope) =>
          onDefaultsChange({ ...defaults, applicabilityScope })
        }
      />

      {/* Footer */}
      <div className="flex items-center justify-between border-t pt-3 mt-0.5">
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t('signalReview.cancel')}
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="link"
            size="sm"
            onClick={onAcceptAll}
            className="text-xs"
          >
            {t('signalReview.acceptAllDefaults')}
          </Button>
          <Button size="sm" onClick={onReviewIndividually} className="gap-1">
            {t('signalReview.reviewIndividually')}
            <ChevronRight className="h-3.5 w-3.5 rtl:rotate-180" />
          </Button>
        </div>
      </div>
    </div>
  );
};
